import * as React from 'react';
import styled from '@emotion/styled';
import {faExclamationCircle} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Ref} from 'react';

export interface SelectOption {
    key: string;
    optGroup?: string;
    show?: boolean;
    value: string;
}

export interface SelectProps {
    className?: string;
    defaultValue?: string;
    disabled?: boolean;
    error?: string;
    hidden?: boolean;
    id: string;
    isColumnPurpose?: boolean;
    label: string;
    name: string;
    onChange?: (key) => void;
    options: SelectOption[];
    ref?: Ref<unknown> | null;
    required?: boolean;
    selectClasses?: string;
    truncate?: boolean;
    value?: string;
}

export const SelectStyles = styled.div`
    .selectColumn {
        background: #fff
            url('data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%274%27 height=%275%27 viewBox=%270 0 4 5%27%3e%3cpath fill=%27%23343a40%27 d=%27M2 0L0 2h4zm0 5L0 3h4z%27/%3e%3c/svg%3e')
            right 0.75rem center / 8px 10px no-repeat;
    }
`;

export const SelectComponent = (
    {
        className,
        defaultValue,
        disabled,
        error,
        hidden,
        id,
        isColumnPurpose = false,
        label,
        name,
        onChange,
        options,
        required,
        selectClasses,
        truncate = false,
        value,
    }: SelectProps,
    ref,
) => {
    const groupedOptions = options.reduce((acc, option) => {
        const accOptionGroup = acc[option?.optGroup] || [];
        return {
            ...acc,
            [option?.optGroup]: [...accOptionGroup, option],
        };
    }, {});

    return (
        <>
            <SelectStyles
                className={`tw-w-full ${truncate ? 'tw-truncate' : ''} ${isColumnPurpose ? '!tw-h-[38px]' : 'tw-relative tw-mb-6'} ${
                    error ? ' tw-mb-0' : ''
                }${hidden ? ' tw-hidden' : ''}${className ? ' ' + className : ''}`}
            >
                <select
                    className={`!tw-z-[auto] ${
                        isColumnPurpose
                            ? 'tw-block tw-w-full !tw-h-[38px] tw-py-1.5 tw-px-3 tw-text-base tw-font-normal tw-leading-6  tw-border tw-border-gray-200 tw-rounded-sm tw-bg-clip-padding'
                            : 'selectColumn tw-appearance-none tw-inline-block tw-w-full tw-py-1.5 tw-pr-7 tw-pl-3 tw-text-base tw-font-normal tw-leading-normal tw-align-middle tw-bg-white tw-border tw-rounded-sm tw-relative !tw-h-[52px] tw-pt-6 tw-z-[2]'
                    } ${disabled ? '!tw-bg-gray-150 tw-text-gray-500 !tw-pointer-events-none' : 'tw-bg-white tw-text-gray-700'} ${
                        error ? `tw-border-red-500` : ``
                    } ${selectClasses}`}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    hidden={hidden}
                    id={id}
                    name={name}
                    onChange={(e) => onChange(e)}
                    ref={ref}
                    required={required}
                    value={value}
                >
                    {Object.keys(groupedOptions)[0] !== `undefined`
                        ? Object.keys(groupedOptions).map((optionGroupName, index) => (
                              <optgroup
                                  key={`${optionGroupName}_${index}`}
                                  label={optionGroupName.toUpperCase()}
                              >
                                  {groupedOptions[optionGroupName]
                                      .filter((option) => option.show !== false)
                                      .map((option, index2) => (
                                          <option
                                              key={`${option.value}_${index2}`}
                                              value={option.key}
                                          >
                                              {option.value}
                                          </option>
                                      ))}
                              </optgroup>
                          ))
                        : options
                              .filter((option) => option?.show !== false)
                              .map((option, index) => (
                                  <option
                                      key={`${option?.key}_${index}`}
                                      value={option?.key}
                                  >
                                      {option?.value}
                                  </option>
                              ))}
                </select>
                {error && (
                    <FontAwesomeIcon
                        className={`!tw-text-red-500 tw-z-10 !tw-text-xl tw-absolute tw-right-8 !tw-top-[15px]`}
                        icon={faExclamationCircle}
                    />
                )}
                <label
                    className={`tw-absolute tw-top-1 tw-left-[13px] tw-text-sm tw-text-gray-500 tw-z-auto${
                        truncate ? ' tw-truncate tw-w-[calc(88%)]' : ''
                    }`}
                    htmlFor={id}
                >
                    {label} {required && ` *`}
                </label>
            </SelectStyles>
            {error && <label className="tw-text-red-500 tw-text-sm tw-font-normal tw-leading-[1.3]">{error}</label>}
        </>
    );
};
export const Select = React.forwardRef(SelectComponent);
